import {
    Grid,
    Theme,
    Typography
} from "@mui/material";
import { FilePond } from 'react-filepond';
import { useDispatch, useSelector } from "react-redux";
import {
    FormikContextType,
    useField,
    useFormikContext
} from "formik";
import { RootStateType } from "../../redux-store/reducers";
import { logout, showError } from "../../redux-store/actions/auth";

export default function DocumentUpload({
    name,
    disabled,
    onFileUpload,
    onFileUploaded,
    minFileSize,
    maxFileSize,
    fileUploadFormats,
    uploadedFile,
    setUploadedFile,
    isFileFormatRestrictionEnabled = true
}: {
    name: string;
    disabled?: boolean;
    onFileUpload: () => void;
    onFileUploaded: () => void;
    minFileSize: string;
    maxFileSize: string;
    fileUploadFormats: string[];
    uploadedFile: any;
    setUploadedFile: (file: any) => void;
    isFileFormatRestrictionEnabled?: boolean
}): JSX.Element {
    const dispatch = useDispatch()
    const { setFieldValue, validateField }: FormikContextType<any> = useFormikContext();
    const [_, meta] = useField(name);
    const errorText = meta.error && meta.touched ? meta.error : '';

    const authToken = useSelector((store: RootStateType) => store.auth.token);

    const handleFileChange = (documentId: string | number) => {
        setFieldValue(name, documentId)
        onFileUploaded();
        validateField(name)
    };

    return (
        <>
            <Grid container
                sx={(theme: Theme) => ({
                    background: theme.palette.common.white,
                    border: `0.5px solid ${theme.palette.text.secondary}80`,
                    padding: uploadedFile?.length ? "0px" : '30px 25px 25px',
                    borderRadius: "10px",
                    rowGap: "10px",
                    '.filepond--root': {
                        fontFamily: `${theme.typography.fontFamily}`,
                    },
                    '.filepond--root.filepond--hopper': {
                        margin: "1px"
                    },
                    '.filepond--panel-root': {
                        background: theme.palette.common.white,
                    },
                    '.filepond--credits': { display: 'none' },
                    '.filepond--label-action': {
                        ...theme.typography.subtitle_500,
                        color: `${theme.palette.primary[500]}`,
                        fontFamily: `${theme.typography.fontFamily}`,
                        letterSpacing: '0.02px',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        textDecoration: "underline",
                        lineHeight: "17.5px"
                    },
                    '.filepond--label-format': {
                        ...theme.typography.subtitle_500,
                        color: `${theme.palette.text.secondary}`,
                        fontWeight: '400',
                        letterSpacing: '0.02px',
                        lineHeight: "17.5px",
                        paddingLeft: '3px',
                    },
                    '.filepond-subheading': {
                        ...theme.typography.subtitle_500,
                        fontWeight: '400',
                        letterSpacing: '0.02px',
                        lineHeight: "17.5px",
                        color: `${theme.palette.text.secondary}`,

                    },
                    '.filepond--drop-label': {
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        pointerEvents: 'all',

                    },
                    '.filepond--file': {
                        color: '#000',
                        flexWrap: 'wrap',
                        fontWeight: 500,
                    },
                    '.filepond--file-wrapper': {
                        backgroundColor: theme.palette.primary[50],
                    },
                    '.delete-icon-container': {
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "primary.100",
                        borderRadius: "50%",
                        padding: "5px",
                        cursor: "pointer"
                    },
                    '.filepond--image-preview-overlay-success': {
                        color: theme.palette.primary[100],
                    },
                    '.filepond--drop-label.filepond--drop-label label': {
                        cursor: disabled ? 'not-allowed' : 'pointer',
                    },
                    '.cursor-style': {
                        cursor: disabled ? 'not-allowed' : 'pointer',
                    }
                })}
            >
                <Grid item xs={12}>
                    <FilePond
                        files={uploadedFile}
                        server={{
                            process: {
                                url: '/api/files/files',
                                method: 'POST',
                                headers: {
                                    Authorization: `Bearer ${authToken}`,
                                },
                                onload: (response): string => {
                                    const result = JSON.parse(response);
                                    if (result?.name && result?.id) {
                                        handleFileChange(result.id);
                                    }
                                    onFileUploaded();
                                    return response;
                                },
                            },
                            revert: null
                        }}
                        onaddfilestart={() => {
                            onFileUpload();
                        }}
                        onaddfile={(error) => {
                            if (error) {
                                onFileUploaded();
                            }
                        }}
                        onprocessfile={(error) => {
                            if (error) {
                                if (error?.code === 401) {
                                    dispatch(showError(error?.body ?? ""))
                                    dispatch(logout());
                                }
                                onFileUploaded();
                            }
                        }}
                        onupdatefiles={(fileItems) => {
                            if (fileItems?.length === 0) {
                                onFileUploaded();
                            }
                            setUploadedFile(fileItems);
                        }}
                        onprocessfilerevert={() => {
                            setUploadedFile([])
                            handleFileChange('');
                        }}
                        name="file"
                        allowPaste={false}
                        minFileSize={minFileSize}
                        maxFileSize={maxFileSize}
                        allowMultiple={false}
                        maxFiles={1}
                        acceptedFileTypes={isFileFormatRestrictionEnabled ? fileUploadFormats : []}
                        labelIdle={
                            `<div class="handle-label-orientation">
                                <div class="filepond-icon cursor-style"><img src="/images/filePondIcon.svg" /></div>
                                <div>
                                <span class="filepond--label-action">Click to Upload</span>
                                <span class="filepond--label-format">
                                ${fileUploadFormats
                                ?.map((format) => {
                                    if (format === "audio/mpeg") {
                                        return "audio/mp3"
                                    }
                                    if (format === "message/rfc822") {
                                        return "message/eml"
                                    }
                                    return format
                                })
                                ?.map((format) => format?.split('/')[1])
                                ?.join(', ')}
                                </span>
                                <div class="filepond-subheading">
                                    ${minFileSize ? `(Min. File Size: ${minFileSize}), ` : ''}(Max. File Size: ${maxFileSize})
                                </div>
                                </div>
                            </div>
                        `}
                        labelFileTypeNotAllowed={'Invalid file type'}
                        labelMaxFileSize={`Max file size is ${maxFileSize}`}
                        labelMinFileSize={`Min file size is ${minFileSize}`}
                        fileValidateTypeLabelExpectedTypesMap={{
                            'image/jpeg': '.jpeg',
                            'image/jpg': '.jpg',
                            'image/png': '.png',
                            'application/pdf': '.pdf',
                        }}
                        imagePreviewHeight={100}
                        disabled={disabled}
                        styleItemPanelAspectRatio="0.2"
                        iconRemove={`
                            <div class="delete-icon-container">
                                <img src="/images/delete-blue.svg"
                            </div>
                        `}
                    >
                    </FilePond>
                </Grid>
            </Grid >
            {
                !!errorText && (
                    <Typography
                        sx={{
                            color: '#d32f2f',
                            fontSize: '0.75rem',
                        }}>
                        {errorText}
                    </Typography>
                )
            }
        </>
    );
}
